import { useState, useEffect, useCallback, useRef } from 'react';
import { notify } from "@/shared/utils/notify";
import { messagesService } from '../../../shared/services';

// Conversaciones se borran después de 2 días
const CONVERSATION_RETENTION_DAYS = 2;
const getTwoDaysAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - CONVERSATION_RETENTION_DAYS);
  return date;
};

// Variable global para el AudioContext (se inicializa después de la primera interacción)
let audioContext: AudioContext | null = null;

// Inicializar AudioContext después de la primera interacción del usuario
const initAudioContext = () => {
  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.log('No se pudo crear AudioContext:', error);
    }
  }
  return audioContext;
};

// Función para reproducir sonido de notificación
const playNotificationSound = () => {
  try {
    const ctx = initAudioContext();
    if (!ctx) return;

    // Si el contexto está suspendido, intentar reanudarlo
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {
        // Si no se puede reanudar, simplemente no reproducir el sonido
        return;
      });
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.3);
  } catch (error) {
    // Silenciar el error, simplemente no reproducir el sonido
  }
};

// Inicializar AudioContext cuando el usuario interactúe con la página
if (typeof window !== 'undefined') {
  const initOnInteraction = () => {
    initAudioContext();
    window.removeEventListener('click', initOnInteraction);
    window.removeEventListener('touchstart', initOnInteraction);
    window.removeEventListener('keydown', initOnInteraction);
  };
  
  window.addEventListener('click', initOnInteraction, { once: true });
  window.addEventListener('touchstart', initOnInteraction, { once: true });
  window.addEventListener('keydown', initOnInteraction, { once: true });
}

export interface IConversation {
  user_id: number;
  user: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  lastMessage?: {
    id: number;
    message: string;
    is_admin: boolean;
    createdAt: string;
  };
  unreadCount?: number;
}

export const useConversations = () => {
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const lastUnreadCountRef = useRef<number>(0);

  const loadConversations = useCallback(async (showLoading = false) => {
    if (showLoading) setIsLoading(true);
    try {
      // Obtener todos los mensajes agrupados por usuario
      const twoDaysAgo = getTwoDaysAgo();
      const response = await messagesService.find({
        query: {
          $sort: { createdAt: -1 },
          $limit: 1000, // Obtener muchos mensajes para agrupar
          // Solo mensajes de los últimos 2 días
          createdAt: { $gte: twoDaysAgo.toISOString() },
        },
      });

      const messages = response.data || [];
      
      // Agrupar por user_id y obtener el último mensaje de cada conversación
      const conversationMap = new Map<number, IConversation>();

      messages.forEach((msg: any) => {
        if (!msg.user_id) return;

        if (!conversationMap.has(msg.user_id)) {
          conversationMap.set(msg.user_id, {
            user_id: msg.user_id,
            user: msg.user || {
              id: msg.user_id,
              first_name: 'Usuario',
              last_name: '',
              email: '',
            },
            unreadCount: 0,
          });
        }

        const conv = conversationMap.get(msg.user_id)!;
        
        // Actualizar último mensaje si es más reciente
        if (!conv.lastMessage || 
            new Date(msg.createdAt || '') > new Date(conv.lastMessage.createdAt)) {
          conv.lastMessage = {
            id: msg.id,
            message: msg.message,
            is_admin: msg.is_admin || false,
            createdAt: msg.createdAt || '',
          };
        }

        // Contar mensajes no leídos (solo mensajes del usuario, no del admin)
        if (!msg.is_admin && !msg.read_at) {
          conv.unreadCount = (conv.unreadCount || 0) + 1;
        }
      });

      // Convertir a array y ordenar por último mensaje
      const conversationsArray = Array.from(conversationMap.values()).sort((a, b) => {
        if (!a.lastMessage && !b.lastMessage) return 0;
        if (!a.lastMessage) return 1;
        if (!b.lastMessage) return -1;
        return new Date(b.lastMessage.createdAt).getTime() - 
               new Date(a.lastMessage.createdAt).getTime();
      });

      // Calcular total de mensajes no leídos
      const totalUnread = conversationsArray.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
      
      // Si hay nuevos mensajes no leídos, reproducir sonido
      if (totalUnread > lastUnreadCountRef.current && lastUnreadCountRef.current > 0) {
        playNotificationSound();
      }
      lastUnreadCountRef.current = totalUnread;

      setConversations(conversationsArray);
    } catch (err: any) {
      // Manejar diferentes tipos de errores
      if (err.code === 404) {
        // Servicio no disponible - podría estar desplegándose aún
        setConversations([]);
        if (showLoading) {
          console.warn('Servicio de mensajes no disponible aún. Verificando...');
        }
      } else if (showLoading) {
        // Otros errores solo en la primera carga
        console.error('Error loading conversations:', err);
        notify.error('Error al cargar las conversaciones');
      }
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, []);

  // Polling para actualizar conversaciones cada 3 segundos (más tiempo real)
  useEffect(() => {
    // Cargar conversaciones iniciales con loading
    loadConversations(true);

    // Iniciar polling más frecuente
    pollingRef.current = setInterval(() => {
      loadConversations(false);
    }, 3000);

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [loadConversations]);

  return {
    conversations,
    isLoading,
    loadConversations,
  };
};
